import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { authedFetch } from '@/lib/authed-fetch';
import {
  slugify as slugifyShared,
  escapeCsv as escapeCsvShared,
  rowsToCsv as rowsToCsvShared,
  formatCostUsd4,
  metricScoreKey as metricScoreKeyShared,
  agentDisplayName as agentDisplayNameShared,
} from '@sentrial/config';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Visibility predicate for the CONVERSATION-THREAD path (ConversationThread's per-step
 * tool-call rows in conversation-thread.tsx). A displayable *tool call* must have a name, so
 * this REQUIRES a truthy toolName and additionally hides the `[SKIPPED]` sentinel. Nameless events
 * (reasoning / decision / state-change / error) are intentionally excluded here — the conversation
 * thread only renders actual tool calls.
 *
 * Contrast with {@link isVisibleEvent}, used by the session-detail graph/messages/panel path, which
 * KEEPS nameless non-tool events and only drops `[SKIPPED]` tool events. Use the right one:
 * a truthy toolName is required for the conversation thread, but NOT for the session-detail views.
 */
export const isVisibleToolEvent = (e: { toolName?: string | null }): boolean =>
  !!e.toolName && !e.toolName.startsWith('[SKIPPED]');

/**
 * Visibility predicate for the SESSION-DETAIL path (SessionGraph, MessagesView, SessionSidebar
 * via useSessionDetail). Only drops the `[SKIPPED]` tool sentinel; it KEEPS events with an empty or
 * absent toolName, because non-tool events (`llm_decision` / `llm_response` / `state_change` /
 * `error`) carry `reasoning`/`eventType` but no toolName and must still render (as graph nodes,
 * messages, and the assistantOutput fallback).
 *
 * Contrast with {@link isVisibleToolEvent} (conversation-thread), which requires a truthy toolName.
 * Requiring a toolName here would silently drop every reasoning/decision/state-change/error node.
 */
export const isVisibleEvent = (e: { toolName?: string | null }): boolean =>
  !(e.toolName ?? '').startsWith('[SKIPPED]');

/**
 * Map a time-range token ('1h' | '6h' | '24h' | '3d' | '7d' | '30d' | 'all') to the cutoff
 * Date beyond which rows are kept (i.e. `Date.now() - range`). Returns `null` for 'all' and
 * for any unrecognized token, so callers treat those as "no filter" (pass everything through).
 * Single source for the client-side createdAt window filter — the experiment pages each used to
 * hand-roll a divergent `hours` lookup + `now - h*60*60*1000` arithmetic; keep new callers here.
 */
const TIME_RANGE_HOURS: Record<string, number> = {
  '1h': 1,
  '6h': 6,
  '24h': 24,
  '3d': 3 * 24,
  '7d': 7 * 24,
  '30d': 30 * 24,
};

export function timeRangeCutoff(range: string): Date | null {
  const hours = TIME_RANGE_HOURS[range];
  if (!hours) return null;
  return new Date(Date.now() - hours * 60 * 60 * 1000);
}

export function formatDuration(ms: number | null): string {
  // Only null/undefined/NaN are "no data" → '-'. A genuine 0ms duration (e.g. a session that
  // took 0ms, or an avgDuration that rounds to 0) must render as a real value ('0ms'), not '-'.
  if (ms == null || Number.isNaN(ms)) return '-';
  if (Math.abs(ms) < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes.toFixed(1)}m`;
  const hours = minutes / 60;
  return `${hours.toFixed(1)}h`;
}

export function formatDate(
  date: string | number | Date | null | undefined,
  month: 'short' | 'long' = 'short'
): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', { month, day: 'numeric', year: 'numeric' });
}

/**
 * Parse a chart date value as LOCAL midnight. A date-only string ("2026-02-27") is otherwise
 * parsed as UTC midnight, which shifts the rendered day in non-UTC timezones — appending
 * `T00:00:00` (no `Z`) parses it in local time. Datetime strings are parsed as-is (time preserved).
 * Single source for the chart components' repeated local-midnight parse.
 */
export function parseLocalDate(value: unknown): Date {
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return new Date(str + 'T00:00:00');
  return new Date(str);
}

/**
 * Short "MMM d" chart axis/tooltip label (e.g. "Feb 27"), parsed at local midnight via
 * parseLocalDate so charts don't each re-derive the same toLocaleDateString + parse workaround.
 * Falls back to the leading date portion of the raw value if unparseable.
 */
export function formatMonthDay(value: unknown): string {
  const date = parseLocalDate(value);
  if (isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * 12-hour clock label ("3:45 PM", or "3:45:22 PM" with `seconds`), en-US. Single source for
 * time-of-day strings — callers were re-deriving toLocaleTimeString / date-fns 'h:mm a' with
 * inconsistent seconds.
 */
export function formatTimeOfDay(
  date: string | number | Date | null | undefined,
  opts: { seconds?: boolean } = {}
): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    ...(opts.seconds ? { second: '2-digit' as const } : {}),
    hour12: true,
  });
}

/**
 * Combined "MMM d, yyyy, h:mm AM/PM" date+time label (e.g. "Feb 27, 2026, 3:45 PM"), en-US. Single source
 * for the "date + time-of-day" rows that pages previously hand-rolled inline via
 * `new Date(x).toLocaleDateString({ month, day, hour, minute })` / `.toLocaleString()`.
 * Composes {@link formatDate} + {@link formatTimeOfDay}, so it inherits their null/invalid-date
 * guard (returns '-' for null/empty/unparseable input) and their consistent en-US formatting,
 * unlike the raw `toLocaleString()` it replaces (which used the visitor's locale and rendered
 * 'Invalid Date' for bad input).
 */
export function formatDateTime(date: string | number | Date | null | undefined): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return `${formatDate(d)}, ${formatTimeOfDay(d)}`;
}

export function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || isNaN(n)) return '0';
  return n.toLocaleString('en-US');
}

/**
 * Format a ratio/percentage value with a fixed decimal precision (default 1) and a
 * trailing '%'. Null/NaN → '0%' (mirroring {@link formatNumber}'s guard) so callers
 * don't each reinvent inline `${x.toFixed(1)}%` with drifting precision. Callers with
 * a distinct null policy (e.g. a '-' placeholder) keep their own null branch around this.
 */
export function formatPercent(value: number | null | undefined, dp = 1): string {
  if (value === null || value === undefined || isNaN(value)) return '0%';
  return `${value.toFixed(dp)}%`;
}

/**
 * Truncate a string to `maxLen` characters, appending a single-character ellipsis ('…') when it is
 * cut. Single home for the display-truncation transform that several pages hand-rolled inline (with
 * drifting '...' vs '…' suffixes). Callers keep their own input-shaping around it (null-fallbacks,
 * `String(x ?? '')` coercion) so those policies stay at the call site that needs them.
 */
export function truncate(text: string, maxLen: number): string {
  return text.length <= maxLen ? text : text.slice(0, maxLen) + '…';
}

/**
 * Humanize a snake_case metric key into a Title-Case label ("first_response" → "First Response").
 * Single source for the custom-metric label transform that the session/metric sidebars each
 * hand-rolled inline.
 *
 * NOTE: this is deliberately NOT identical to the api-side `formatMetricName` in
 * rca/agent-report-render.ts. This web copy title-cases EVERY word and does NOT split camelCase
 * ("helpfulness_score" → "Helpfulness Score", "responseTime" → "ResponseTime" — interior capitals
 * are preserved, camelCase is simply not split), matching the Title-Case label convention of the
 * web sidebars. The API copy instead splits camelCase and capitalizes only the first character of
 * the whole string ("responseTime" → "Response Time", "helpfulness_score" → "Helpfulness score")
 * for its RCA report. A camelCase/multi-word custom
 * metric therefore renders a different label in the web sidebars than in the API RCA report;
 * this divergence is by design (each surface follows its own casing convention). Only migrates the
 * Title-Case call sites — call sites that render a lowercased `_`→space label must keep their own
 * transform to avoid a visible label change.
 */
export function formatMetricName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Format a dollar cost with a consistent 4-decimal precision. Null/NaN → '$0.0000'
 * so callers don't each reinvent inline `$${x.toFixed(n)}` with drifting precision.
 * Thin web-side wrapper over the shared numeric core `formatCostUsd4` (@sentrial/config);
 * only the null/NaN sentinel differs from the api-side `formatCostUsd` ('N/A').
 */
export function formatCost(cost: number | null | undefined): string {
  return formatCostUsd4(cost) ?? '$0.0000';
}

/**
 * Canonical agent display name: prefers the human `displayName`, falls back to the internal `name`,
 * then to `fallback` ('Unknown' by default). This is the SINGLE shared helper for the agent display
 * string across the web surfaces so a renamed agent renders consistently instead of each call site
 * hand-rolling `displayName || name` with a drifting sentinel — prefer it for every call site (pass
 * a page-specific `fallback` like '-'/'Agent'/'' where the surface needs one). Preserves `||` (not
 * `??`) falsiness so ''/null displayName still falls through to name. Single-sourced in
 * @sentrial/config so it cannot drift from the server's `agentLabel()`
 * (apps/api/src/lib/agents/agent-label.ts); the parity is guarded by
 * tests/unit/web/agent-label-parity.test.ts.
 */
export const agentDisplayName = agentDisplayNameShared;

/**
 * Compact relative time for dashboard timestamps: "just now" / "5m ago" / "3h ago" / "2d ago",
 * then falls back to an absolute date past 30 days. This is the SINGLE source for "X ago" strings
 * across the app — deliberately terser than date-fns' `formatDistanceToNow` ("about 3 hours ago"),
 * which reads too long in dense tables; don't reintroduce date-fns for relative time.
 */
export function formatRelativeTime(date: string | number | Date | null | undefined): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(d);
}

/**
 * Convert a string into a URL-safe slug (lowercase, non-alphanumeric runs → single hyphen,
 * trimmed hyphens). Used by the org-name slug PREVIEW in settings so the shown slug matches
 * what the server will actually generate. Single-sourced in @sentrial/config so the web preview
 * and the API-side slug generator cannot drift; re-exported here as the web-facing name.
 */
export const slugify = slugifyShared;

/**
 * RFC-4180 CSV cell escaper (also neutralizes spreadsheet formula/DDE injection). Single-sourced in
 * @sentrial/config so it cannot drift from the API-side escaper; re-exported under the web-facing
 * `escapeCSV` name as public utils surface. The sessions/offline-experiment CSV exports go through
 * {@link rowsToCSV} (which escapes each cell internally), not this helper directly.
 */
export const escapeCSV = escapeCsvShared;

/**
 * Serialize a matrix of cells into a CSV string, escaping each cell via {@link escapeCSV}.
 * Single-sourced in @sentrial/config; re-exported under the web-facing `rowsToCSV` name.
 */
export const rowsToCSV = rowsToCsvShared;

/**
 * Canonical "custom-metric name → snake_case key" transform for READING the stored
 * `metricScores.customMetrics[...]` blob. Single-sourced in @sentrial/config so this reader
 * cannot drift from the scorer's writer key (a punctuation-stripping mismatch would silently
 * read back "No data" for punctuated metric names). Re-exported under the web-facing name.
 */
export const metricScoreKey = metricScoreKeyShared;

/**
 * Abbreviate a non-negative count for compact display: >=1000 → "X.Xk", otherwise the locale-
 * formatted integer. Single canonical rule shared by `formatTokenCount` (waterfall-events) and the
 * chart y-axis tick formatters so the two can't drift. A valid Recharts tickFormatter return.
 */
export function abbreviateCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString();
}

/**
 * Y-axis tick formatter shared across charts. Only the >=1000 "X.Xk" abbreviation is shared with
 * formatTokenCount (via abbreviateCount); values below 1000 are stringified RAW via String() so
 * small-float series (cost in $, sub-second latency, e.g. 0.0005 → "0.0005") don't get rounded to 3
 * fraction digits by abbreviateCount's toLocaleString fallback. String() does no rounding, and
 * recharts' tickFormatter requires a string return. Kept as an exported pure helper (not an inline
 * lambda) so the sub-1000 raw-passthrough behavior can be locked in by a test.
 */
export function formatYAxisTick(value: number): string {
  return value >= 1000 ? abbreviateCount(value) : String(value);
}

/**
 * Trigger a browser download of in-memory data as a file. Owns the
 * createObjectURL → temporary <a> → click → revokeObjectURL dance so callers
 * (CSV/JSON exports across experiments, research, datasets, ...) don't each
 * re-derive the same boilerplate with drifting cleanup. The temporary anchor is
 * appended to and removed from the document so the click works across browsers.
 */
export function downloadBlob(data: BlobPart, filename: string, type = 'text/csv'): void {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export type OccurrenceLabel = 'correct' | 'incorrect';

/**
 * POST a training-grade human verdict for a signal occurrence (D3 flywheel).
 * Shared by the signals page and the review queue so the endpoint URL, body shape,
 * auth header, and error extraction live in one place. Callers own their own
 * toast copy and cache invalidation / optimistic-update logic.
 */
export async function postOccurrenceLabel(
  orgId: string,
  signalId: string,
  occurrenceId: string,
  label: OccurrenceLabel
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const res = await authedFetch(
      `/api/organizations/${orgId}/signals/${signalId}/occurrences/${occurrenceId}/feedback`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ label }),
      }
    );
    if (res.ok) return { ok: true };
    const body = await res.json().catch(() => ({}));
    return { ok: false, error: body.error || 'Failed to record label' };
  } catch {
    return { ok: false, error: 'Failed to record label' };
  }
}

/**
 * POST an operator's ack/dismiss of a signal occurrence's alert (source 'ack',
 * D flywheel). Acknowledging = alert was real; dismissing = false positive.
 * Distinct from postOccurrenceLabel's explicit 'human' review verdict.
 */
export async function postOccurrenceAck(
  orgId: string,
  signalId: string,
  occurrenceId: string,
  acknowledged: boolean
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const res = await authedFetch(
      `/api/organizations/${orgId}/signals/${signalId}/occurrences/${occurrenceId}/ack`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ acknowledged }),
      }
    );
    if (res.ok) return { ok: true };
    const body = await res.json().catch(() => ({}));
    return { ok: false, error: body.error || 'Failed to record ack' };
  } catch {
    return { ok: false, error: 'Failed to record ack' };
  }
}

/** Minimal shape needed to group sessions into conversations. */
export interface ConvoGroupable {
  id: string;
  convoId?: string | null;
  createdAt: string;
}

export interface ConvoGroup<T> {
  /** The convoId for multi-turn groups, or a synthetic key for standalone sessions. */
  convoId: string;
  /** Representative session: earliest createdAt for multi-turn groups, else the lone member. */
  firstTurn: T;
  members: T[];
  groupSize: number;
}

/**
 * Pick the member with the earliest createdAt. Requires a non-empty array —
 * reducing an empty array would return `members[0]` (undefined), which the `T`
 * return type would silently lie about. Callers must pre-check length.
 */
export function earliestByCreatedAt<T extends { createdAt: string }>(members: T[]): T {
  if (members.length === 0) {
    throw new Error('earliestByCreatedAt requires a non-empty array');
  }
  return members.reduce(
    (earliest, s) =>
      new Date(s.createdAt).getTime() < new Date(earliest.createdAt).getTime() ? s : earliest,
    members[0]
  );
}

/**
 * Group sessions into conversations by convoId. A "conversation" is a convoId
 * SHARED BY >1 session (every session defaults its convoId to its own id, so
 * presence alone is meaningless). Single-member groups are returned with
 * groupSize 1 so callers can render them as standalone rows; multi-turn groups
 * expose the earliest-createdAt member as firstTurn. Group order follows first
 * appearance in the input, preserving the caller's existing sort.
 *
 * NOTE: the org sessions API route (apps/api/.../sessions/route.ts) keeps its
 * own copy of this same >1 threshold and earliest-createdAt selection for the
 * server-rendered grouping (the exported groupPageSessionsByConvo in
 * apps/api/src/lib/services/sessions.ts). The two are intentionally aligned but
 * are NOT a single shared source (separate apps); their parity on (a) which
 * convoIds form multi-turn conversations and (b) each conversation's
 * earliest-createdAt first turn is enforced by
 * tests/unit/web/convo-grouping-parity.test.ts, which fails CI on drift.
 */
export function groupSessionsByConvo<T extends ConvoGroupable>(
  sessions: T[]
): Array<ConvoGroup<T>> {
  const groups = new Map<string, T[]>();
  const order: string[] = [];
  for (const s of sessions) {
    const key = s.convoId || `__single__${s.id}`;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(s);
  }
  return order.map((key) => {
    const members = groups.get(key)!;
    const firstTurn = members.length > 1 ? earliestByCreatedAt(members) : members[0];
    return { convoId: key, firstTurn, members, groupSize: members.length };
  });
}
